// KCOS Phase 6 — Privacy & Control Center.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { getPreferences, savePreferences, exportMyData, type KcosPreferences } from "@/lib/preferences.functions";
import { clearDismissals } from "@/lib/recommendations.functions";

export const Route = createFileRoute("/_authenticated/settings/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy & Personalization · KAfrequency" },
      { name: "description", content: "Control how KAfrequency personalizes your journey, timeline, and recommendations." },
    ],
  }),
  component: PrivacyCenter,
});

function PrivacyCenter() {
  const load = useServerFn(getPreferences);
  const save = useServerFn(savePreferences);
  const doExport = useServerFn(exportMyData);
  const doClear = useServerFn(clearDismissals);
  const [prefs, setPrefs] = useState<Awaited<ReturnType<typeof getPreferences>> | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    load().then(setPrefs).catch(() => setPrefs(null));
  }, [load]);

  const persist = async (patch: KcosPreferences) => {
    if (!prefs) return;
    const next = { ...prefs, ...patch, reco_categories: { ...prefs.reco_categories, ...(patch.reco_categories ?? {}) } };
    setPrefs(next);
    setSaving(true);
    try {
      await save({ data: patch });
      setMsg("Saved.");
    } catch {
      setMsg("Could not save. Try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 1500);
    }
  };

  const exportJson = async () => {
    const data = await doExport();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kafrequency-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!prefs) return <div className="text-sm text-foreground/60">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-2xl text-gold-luminous">Privacy & Personalization</h1>
      <p className="mt-2 text-sm text-foreground/70">
        Control what KCOS — the KAfrequency Consciousness Operating System — remembers, surfaces, and shares.
      </p>

      <Card className="mt-6 divide-y hairline p-0">
        <Toggle
          label="AI personalization"
          desc="Let the ecosystem tailor recommendations to your journey. When off, everyone sees the same editorial defaults."
          checked={prefs.ai_personalization}
          onChange={(v) => persist({ ai_personalization: v })}
        />
        <Toggle
          label="Include journal in timeline"
          desc="Show journal entries alongside gates, activations, and challenges on your Timeline. Titles only, never body text."
          checked={prefs.timeline_include_journal}
          onChange={(v) => persist({ timeline_include_journal: v })}
        />
        <div className="p-4">
          <div className="text-sm text-foreground/90">Profile visibility</div>
          <div className="text-xs text-foreground/60">Who can see your public profile page.</div>
          <select
            className="mt-2 rounded-md bg-black/40 border hairline px-3 py-2 text-sm"
            value={prefs.profile_visibility}
            onChange={(e) => persist({ profile_visibility: e.target.value as KcosPreferences["profile_visibility"] })}
          >
            <option value="private">Private — only me</option>
            <option value="members">Members — signed-in seekers</option>
            <option value="public">Public — anyone</option>
          </select>
        </div>
      </Card>

      <h2 className="mt-8 font-serif text-lg text-gold-luminous">Recommendation categories</h2>
      <p className="text-xs text-foreground/60">Choose what kinds of suggestions can appear.</p>
      <Card className="mt-3 divide-y hairline p-0">
        {(["gate", "activation", "release", "book", "wisdom"] as const).map((k) => (
          <Toggle
            key={k}
            label={LABELS[k]}
            desc=""
            checked={prefs.reco_categories[k] !== false}
            onChange={(v) => persist({ reco_categories: { [k]: v } })}
          />
        ))}
      </Card>

      <h2 className="mt-8 font-serif text-lg text-gold-luminous">Your data</h2>
      <div className="mt-3 flex flex-wrap gap-3">
        <button onClick={exportJson} className="rounded-md border hairline px-4 py-2 text-sm hover:bg-black/40">
          Export my data (JSON)
        </button>
        <button
          onClick={() => doClear().then(() => setMsg("Dismissed recommendations restored."))}
          className="rounded-md border hairline px-4 py-2 text-sm hover:bg-black/40"
        >
          Restore dismissed recommendations
        </button>
      </div>

      <p className="mt-6 text-xs text-foreground/50 min-h-[1em]">
        {saving ? "Saving…" : msg ?? ""}
      </p>
    </div>
  );
}

const LABELS: Record<string, string> = {
  gate: "Gates",
  activation: "Activations",
  release: "Music & frequencies",
  book: "Books",
  wisdom: "Wisdom teachings",
};

function Toggle({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start justify-between gap-4 p-4 cursor-pointer">
      <div>
        <div className="text-sm text-foreground/90">{label}</div>
        {desc && <div className="text-xs text-foreground/60">{desc}</div>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-[color:var(--gold,#c9a24b)]"
      />
    </label>
  );
}
